import React, { useEffect, useRef, useState } from 'react';
import styles from './phonedialer.module.css';
import { getFetch, postFetch } from '../../fetch/fetch';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Switch from '@mui/joy/Switch';
import { CssVarsProvider } from '@mui/joy/styles';

const DEFAULT_MENT = '독학재수학원 수능선배입니다';

const NUMPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '다시입력', '0', '⌫'];

const REP_NUMBERS = [
    { label: '1668-5786', value: '1668-5786', accountId: 1 },
    { label: '1555-2591', value: '1555-2591', accountId: 2 },
];

interface Counselor {
    accountId: number;
    happytalkId: string;
    title: string;
    phoneNumber: string;
}

interface UserProp {
    phoneNumber?: string;
    location?: string;
    value: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    user?: UserProp | null;
}

const PhoneDialer: React.FC<Props> = ({ open, onClose, user }) => {

    const [counselors, setCounselors] = useState<Counselor[]>([]);
    const [repNumber, setRepNumber] = useState('1668-5786');
    const [cusTel, setCusTel] = useState('');
    const [ani, setAni] = useState('010');
    const [outcallMent, setOutcallMent] = useState(DEFAULT_MENT);
    const [mentEnabled, setMentEnabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [activeCall, setActiveCall] = useState<{ cdrId: any; callerNo: string; calleeNo: string } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const accountId = REP_NUMBERS.find(r => r.value === repNumber)?.accountId || 1;
    const filteredCounselors = counselors.filter(c => c.accountId === accountId);

    // 상담사 목록 로드
    useEffect(() => {
        const load = async () => {
            try {
                const result: any = await getFetch('/happytalk/counselors');
                if (result.message === 'success' && result.data.length > 0) {
                    setCounselors(result.data);
                }
            } catch (e) {
                console.log(e);
            }
        };
        load();
    }, []);

    // user 로드 시 발신대표번호 default 결정
    useEffect(() => {
        if (!user) return;
        const defaultRep = user.location === '대구점' ? '1555-2591' : '1668-5786';
        setRepNumber(defaultRep);
    }, [user]);

    // 발신대표번호 또는 상담사 목록 변경 시 cusTel default 재계산
    useEffect(() => {
        if (filteredCounselors.length === 0) return;

        // 1순위: 내 전화번호와 동일한 상담사 (같은 계정 내에서)
        const byPhone = filteredCounselors.find(c => c.phoneNumber === user?.phoneNumber);
        if (byPhone) {
            setCusTel(byPhone.phoneNumber);
            return;
        }

        // 2순위: location 앞 2글자와 title 앞 2글자가 동일한 상담사
        const locationPrefix = user?.location?.slice(0, 2);
        if (locationPrefix) {
            const byLocation = filteredCounselors.find(c => c.title.slice(0, 2) === locationPrefix);
            if (byLocation) {
                setCusTel(byLocation.phoneNumber);
                return;
            }
        }

        // 3순위: 첫 번째 항목
        setCusTel(filteredCounselors[0].phoneNumber);
    }, [repNumber, counselors, user]);

    // 패널 열릴 때 ani 초기화 + 포커스
    useEffect(() => {
        if (open) {
            setAni('010');
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [open]);

    if (!open) return null;

    const handleNum = (key: string) => {
        if (key === '다시입력') {
            setAni('010');
        } else if (key === '⌫') {
            setAni(prev => prev.slice(0, -1));
        } else {
            if (ani.length >= 11) return;
            setAni(prev => prev + key);
        }
    };

    const formatDisplay = (num: string) => {
        if (num.length <= 3) return num;
        if (num.length <= 7) return `${num.slice(0, 3)}-${num.slice(3)}`;
        return `${num.slice(0, 3)}-${num.slice(3, 7)}-${num.slice(7)}`;
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (/^\d$/.test(e.key)) {
            e.preventDefault();
            if (ani.length < 11) setAni(prev => prev + e.key);
        } else if (e.key === 'Backspace') {
            e.preventDefault();
            setAni(prev => prev.slice(0, -1));
        } else if (e.key !== 'Tab') {
            e.preventDefault();
        }
    };

    const handleCall = async () => {
        if (!ani || !cusTel || loading) return;
        setLoading(true);
        try {
            const body: any = { cusTel, ani, accountId };
            if (mentEnabled) body.outcallMent = outcallMent;

            const result: any = await postFetch('/happytalk/outcall', body);
            if (result.message === 'success') {
                toast.success('통화 연결 요청이 완료되었습니다.', {
                    position: "bottom-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                    theme: "colored"
                });
                setActiveCall({
                    cdrId: result.data?.cdrId,
                    callerNo: result.data?.callerNo,
                    calleeNo: result.data?.calleeNo,
                });
                setAni('010');
            } else {
                toast.error('통화 연결 실패: ' + result.message, {
                    position: "bottom-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                    theme: "colored"
                });
            }
        } catch (e) {
            console.log(e);
            toast.error('오류가 발생했습니다.', {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: true,
                theme: "colored"
            });
        }
        setLoading(false);
    };

    const handleCancel = async () => {
        if (!activeCall || loading) return;
        setLoading(true);
        try {
            const result: any = await postFetch('/happytalk/cancel-outcall', {
                cdrId: activeCall.cdrId,
                callerNo: activeCall.callerNo,
                calleeNo: activeCall.calleeNo,
                accountId,
            });
            if (result.message === 'success') {
                toast.success('통화가 종료되었습니다.', {
                    position: "bottom-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                    theme: "colored"
                });
            } else {
                toast.error('통화 종료 실패: ' + result.message, {
                    position: "bottom-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                    theme: "colored"
                });
            }
        } catch (e) {
            console.log(e);
            toast.error('오류가 발생했습니다.', {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: true,
                theme: "colored"
            });
        }
        setActiveCall(null);
        setLoading(false);
    };

    return (
        <div className={styles.panel}>

            <div className={styles.header}>
                <span className={styles.title}>전화걸기</span>
                <button className={styles.closeBtn} onClick={onClose}>✕</button>
            </div>

            <div className={styles.section}>
                <div className={styles.label}>발신대표번호 설정</div>
                <CssVarsProvider>
                    <Select
                        value={repNumber}
                        onChange={(_, val) => val && setRepNumber(val as string)}
                        size="sm"
                        sx={{ width: '100%' }}
                        disabled={!!activeCall}
                    >
                        {REP_NUMBERS.map(r => (
                            <Option key={r.value} value={r.value}>{r.label}</Option>
                        ))}
                    </Select>
                </CssVarsProvider>
            </div>

            <div className={styles.section}>
                <div className={styles.label}>상담사 번호</div>
                <CssVarsProvider>
                    <Select
                        value={cusTel}
                        onChange={(_, val) => val && setCusTel(val as string)}
                        size="sm"
                        sx={{ width: '100%' }}
                    >
                        {filteredCounselors.map(c => (
                            <Option key={c.happytalkId} value={c.phoneNumber}>{c.title} ({formatDisplay(c.phoneNumber)})</Option>
                        ))}
                    </Select>
                </CssVarsProvider>
            </div>

            <input
                ref={inputRef}
                className={styles.displayInput}
                value={formatDisplay(ani)}
                onKeyDown={handleKeyDown}
                onChange={() => {}}
                placeholder="고객 전화번호 입력"
            />

            <div className={styles.numpad}>
                {NUMPAD.map(key => (
                    <button
                        key={key}
                        className={`${styles.numKey} ${key === '다시입력' ? styles.numKeySmall : ''}`}
                        onClick={() => handleNum(key)}
                    >
                        {key}
                    </button>
                ))}
            </div>

            <div className={styles.mentSection}>
                <div className={styles.mentHeader}>
                    <span className={styles.mentLabel}>아웃콜 멘트 설정</span>
                    <CssVarsProvider>
                        <Switch
                            checked={mentEnabled}
                            onChange={e => setMentEnabled(e.target.checked)}
                            color={mentEnabled ? 'success' : 'neutral'}
                            size="sm"
                        />
                    </CssVarsProvider>
                </div>
                {mentEnabled && (
                    <>
                        <textarea
                            className={styles.mentTextarea}
                            value={outcallMent}
                            onChange={e => {
                                if (e.target.value.length <= 50) setOutcallMent(e.target.value);
                            }}
                            rows={2}
                        />
                        <div className={styles.mentCount}>{outcallMent.length}/50</div>
                    </>
                )}
            </div>

            {activeCall ? (
                <button
                    className={`${styles.callBtn} ${styles.callBtnCancel} ${loading ? styles.callBtnDisabled : ''}`}
                    onClick={handleCancel}
                    disabled={loading}
                >
                    {loading ? '처리 중...' : '통화 끊기'}
                </button>
            ) : (
                <button
                    className={`${styles.callBtn} ${(!ani || loading) ? styles.callBtnDisabled : ''}`}
                    onClick={handleCall}
                    disabled={!ani || loading}
                >
                    {loading ? '연결 중...' : '통화 걸기'}
                </button>
            )}

        </div>
    );
};

export default PhoneDialer;
